import { PublishedSiteSnapshot, DomainMapping } from '../types';
import { realtimeDbService as dbService } from './realtimeDbService';
import { normalizeDomain } from '../utils/security';

export interface SiteResolutionResult {
  success: boolean;
  siteId?: string;
  normalizedDomain: string;
  snapshot?: PublishedSiteSnapshot | null;
  status: 'PUBLISHED' | 'SUSPENDED' | 'EXPIRED' | 'NOT_FOUND' | 'ERROR';
  isCustomDomain: boolean;
  redirectUrl?: string;
  error?: string;
}

interface CacheEntry {
  result: SiteResolutionResult;
  timestamp: number;
}

export class SiteResolverService {
  private static cache = new Map<string, CacheEntry>();
  private static CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes client cache

  /**
   * Synchronously retrieves cached site resolution from memory or sessionStorage for 0ms instant render
   */
  public static getCachedSite(hostnameOrQuery?: string): SiteResolutionResult | null {
    if (!hostnameOrQuery) return null;
    const rawTarget = hostnameOrQuery.trim();
    const normalized = normalizeDomain(rawTarget);

    const cached = this.cache.get(normalized);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      return cached.result;
    }

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const stored = sessionStorage.getItem(`res_cache_${normalized}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.timestamp && Date.now() - parsed.timestamp < this.CACHE_TTL_MS && parsed.result) {
            this.cache.set(normalized, { result: parsed.result, timestamp: parsed.timestamp });
            return parsed.result;
          }
        }
      } catch {
        // Safe fallback
      }
    }
    return null;
  }

  /**
   * Resolves a public site by hostname or subdomain query
   */
  public static async resolveSite(hostnameOrQuery: string): Promise<SiteResolutionResult> {
    const rawTarget = hostnameOrQuery.trim();
    const normalized = normalizeDomain(rawTarget);

    // 1. Check in-memory fast cache
    const cached = this.cache.get(normalized);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      return cached.result;
    }

    // 2. Check sessionStorage fast cache for instant FCP across browser navigation
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const stored = sessionStorage.getItem(`res_cache_${normalized}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.timestamp && Date.now() - parsed.timestamp < this.CACHE_TTL_MS && parsed.result) {
            this.cache.set(normalized, { result: parsed.result, timestamp: parsed.timestamp });
            return parsed.result;
          }
        }
      } catch {
        // Safe fallback if sessionStorage is blocked
      }
    }

    try {
      // 3. Stage 1: Parallel lookup across domain mapping, slug, and direct siteId
      let siteId: string | null = null;
      let isCustomDomain = false;

      // Prepare candidate queries in parallel
      const rootVariant = normalized.replace(/^www\./i, '');
      const mappingPromise = dbService.getDomainMapping(normalized);
      const rootMappingPromise = rootVariant !== normalized ? dbService.getDomainMapping(rootVariant) : Promise.resolve(null);
      const slugPromise = dbService.getSiteBySlugOrDomain(normalized);
      const rootSlugPromise = rootVariant !== normalized ? dbService.getSiteBySlugOrDomain(rootVariant) : Promise.resolve(null);

      let subdomain = normalized;
      if (normalized.includes('.adiosoft.')) {
        subdomain = normalized.split('.')[0];
      } else if (normalized.startsWith('site-') || !normalized.includes('.')) {
        subdomain = normalized;
      }
      const subdomainPromise = subdomain !== normalized ? dbService.getSiteBySubdomain(subdomain) : Promise.resolve(null);
      const directIdPromise = dbService.getSiteById(rawTarget);

      const [mapping, rootMapping, siteBySlug, siteByRootSlug, siteBySubdomain, siteByIdDirect] = await Promise.all([
        mappingPromise,
        rootMappingPromise,
        slugPromise,
        rootSlugPromise,
        subdomainPromise,
        directIdPromise,
      ]);

      if (mapping && mapping.siteId) {
        siteId = mapping.siteId;
        isCustomDomain = true;
      } else if (rootMapping && rootMapping.siteId) {
        siteId = rootMapping.siteId;
        isCustomDomain = true;
      } else if (siteBySlug) {
        siteId = siteBySlug.id;
        isCustomDomain = false;
      } else if (siteByRootSlug) {
        siteId = siteByRootSlug.id;
        isCustomDomain = false;
      } else if (siteBySubdomain) {
        siteId = siteBySubdomain.id;
        isCustomDomain = false;
      } else if (siteByIdDirect) {
        siteId = siteByIdDirect.id;
        isCustomDomain = false;
      }

      // If still not resolved, query backend resolve API directly
      if (!siteId && typeof window !== 'undefined' && typeof fetch !== 'undefined') {
        try {
          const apiRes = await fetch(`/api/domains/resolve?domain=${encodeURIComponent(normalized)}`);
          if (apiRes.ok) {
            const apiData = await apiRes.json();
            const resolvedId = apiData?.websiteId || apiData?.siteId;
            if (resolvedId) {
              siteId = resolvedId;
              isCustomDomain = true;
            }
          }
        } catch {
          // ignore
        }
      }

      if (!siteId) {
        const notFoundResult: SiteResolutionResult = {
          success: false,
          normalizedDomain: normalized,
          status: 'NOT_FOUND',
          isCustomDomain,
          error: 'Belirtilen alan adına bağlı aktif bir web sitesi bulunamadı.',
        };
        return notFoundResult;
      }

      // 4. Stage 2: Parallel fetch of published snapshot and site status
      const [snapshot, site] = await Promise.all([
        dbService.getPublishedSnapshot(siteId),
        dbService.getSiteById(siteId),
      ]);

      if (site) {
        if (site.status === 'SUSPENDED') {
          return {
            success: false,
            siteId,
            normalizedDomain: normalized,
            status: 'SUSPENDED',
            isCustomDomain,
            redirectUrl: 'https://www.adiosoft.com/',
            error: 'Bu web sitesinin yayın süresi sona ermiş veya askıya alınmıştır.',
          };
        }

        if (site.status === 'UNPUBLISHED') {
          return {
            success: false,
            siteId,
            normalizedDomain: normalized,
            status: 'NOT_FOUND',
            isCustomDomain,
            redirectUrl: isCustomDomain ? 'https://www.adiosoft.com/' : undefined,
            error: 'Bu web sitesi şu anda yayında değildir (Unpublished).',
          };
        }
      }

      // If published snapshot exists, serve published snapshot immediately
      let resolvedSnapshot = snapshot;
      if (!resolvedSnapshot && site) {
        resolvedSnapshot = {
          versionId: 'v1_auto',
          siteId,
          publishedAt: site.publishedAt || site.createdAt || Date.now(),
          publishedBy: site.ownerId || '',
          name: site.name || 'Web Sitesi',
          industry: site.industry,
          templateId: site.templateId,
          templateVersion: 1,
          theme: {
            primaryColor: site.settings?.primaryColor || '#0284C7',
            secondaryColor: site.settings?.secondaryColor || '#0F172A',
            accentColor: site.settings?.accentColor || '#38BDF8',
            fontFamily: site.settings?.fontFamily || 'Inter, sans-serif',
            headingFont: site.settings?.headingFont || 'Plus Jakarta Sans, sans-serif',
            logoUrl: site.settings?.logoUrl || '',
            logoText: site.settings?.logoText || site.name,
          },
          settings: site.settings,
          navigation: (site.pages || []).map((p, i) => ({ id: p.id, title: p.title, slug: p.slug, isHomePage: p.isHomePage, order: i })),
          pages: site.publishedPages && site.publishedPages.length > 0 ? site.publishedPages : (site.pages || []),
          seo: site.settings?.seo || { title: site.name },
          status: 'PUBLISHED',
        };
      }

      if (!resolvedSnapshot) {
        return {
          success: false,
          siteId,
          normalizedDomain: normalized,
          status: 'NOT_FOUND',
          isCustomDomain,
          error: 'Bu web sitesinin yayınlanmış içeriği bulunamadı.',
        };
      }

      const result: SiteResolutionResult = {
        success: true,
        siteId,
        normalizedDomain: normalized,
        snapshot: resolvedSnapshot,
        status: 'PUBLISHED',
        isCustomDomain,
      };

      // Store in memory and sessionStorage
      this.cache.set(normalized, {
        result,
        timestamp: Date.now(),
      });

      if (typeof window !== 'undefined' && window.sessionStorage) {
        try {
          sessionStorage.setItem(
            `res_cache_${normalized}`,
            JSON.stringify({ result, timestamp: Date.now() })
          );
        } catch {
          // ignore storage quota
        }
      }

      return result;
    } catch (err: any) {
      console.error('[SiteResolver Error]:', err);
      return {
        success: false,
        normalizedDomain: normalized,
        status: 'ERROR',
        isCustomDomain: false,
        error: err.message || 'Domain çözümlenirken teknik bir hata oluştu.',
      };
    }
  }

  /**
   * Invalidates resolution cache for a domain
   */
  public static invalidateCache(domain: string): void {
    const normalized = normalizeDomain(domain);
    this.cache.delete(normalized);
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.removeItem(`res_cache_${normalized}`);
      } catch {
        // ignore
      }
    }
  }

  /**
   * Clears the entire resolver cache
   */
  public static clearCache(): void {
    this.cache.clear();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        Object.keys(sessionStorage).forEach((k) => {
          if (k.startsWith('res_cache_')) {
            sessionStorage.removeItem(k);
          }
        });
      } catch {
        // ignore
      }
    }
  }
}
